/**
 * 20261029 PC 핸즈업 믹서 입력 clone·마이캠 캡처 반환 (web-relay R-246·R-247, milestone_42 §3.22~§3.32 1차)
 *
 * 왜 있나: 믹서 입력 노드는 원본 스트림의 clone 으로 만든다(마이크, 화면 시스템 소리 — 화면 video 포함).
 *          노드를 disconnect 하고 참조만 비우면 clone 트랙이 살아 있어 Home(/main, SPA)에서
 *          크롬 마이크·화면 공유 표시가 남는다. 전체 문서 이동 경로에서는 문서 교체가 이를 가려 줬다.
 *
 * 계약
 * - 생성: clone 을 한 번 만들어 그 객체로 노드를 만들고, node 와 쌍으로 등록한다(R-254).
 *         노드 생성이 실패하면 만든 clone 만 반환하고 원래 오류를 그대로 던진다(R-256).
 * - 반환: 등록된 쌍의 clone 트랙 전체를 멱등 정지한다. 원본·송출·destination·Producer 는 대상이 아니다.
 * - 종료: 종료 시작(onBegin) 때 등록 쌍·현재 노드·마이캠 스트림을 스냅샷한다(R-257).
 *         종료 중 역할별 정리는 스냅샷에 든 노드만 정리하고(R-258), 마지막에 스냅샷 전체를 반환한다.
 *         스냅샷 뒤 새로 등록된 쌍은 이번 종료에서 정지·제거하지 않는다.
 * - 실패: 종료 중 실패는 스냅샷 실패 목록에 모아 종료 결과까지 전달한다(R-252).
 * 앱 웹뷰(Android/iOS)·C3 는 믹서 노드를 만들지 않으므로 등록부가 비어 있다. 마이캠은 웹뷰에서 건드리지 않는다.
 */

const ROLE_KEYS = {
    mic   : { node: "micStreamSource", gain: "micGainNode", flag: "isMicConnectFlag", has: "hasMicAudio" },
    system: { node: "systemStreamSource", gain: "systemGainNode", flag: "isSysConnectFlag", has: "hasSystemAudio" }
};

const registry = new Map();      // node → { node, clone, role }
let getInfo = () => null;        // HandsUpClient 의 handsUpClientInfo
let exitSnapshot = null;         // { token, nodes:Set, myCam:{el, stream}|null, failures:[], released }
const releasedNodes = new WeakSet();

const log = (msg) => console.log(`HandsUpMixerInputCleanup - ${msg}`);

const stopAllTracks = (stream, failures, name) => {
    if (!stream || typeof stream.getTracks !== "function") return;
    let tracks = [];
    try {
        tracks = stream.getTracks();
    } catch (err) {
        failures.push({ name, err });
        return;
    }
    tracks.forEach((track) => {
        try {
            if (track && track.readyState !== "ended") track.stop();
        } catch (err) {
            failures.push({ name, err });
        }
    });
};

const recordFailures = (failures) => {
    if (failures.length === 0) return;
    if (exitSnapshot !== null) {
        failures.forEach((f) => exitSnapshot.failures.push(f));
    }
    log(`정리 실패 ${failures.map((f) => f.name).join(",")}`);
};

const releaseEntry = (entry, failures) => {
    registry.delete(entry.node);
    releasedNodes.add(entry.node);
    try {
        entry.node.disconnect();
    } catch (err) {
        // 이미 연결이 끊긴 노드는 오류가 날 수 있다. 캡처 반환과는 별개다.
    }
    stopAllTracks(entry.clone, failures, `${entry.role}-clone`);
};

/** HandsUpClient 가 정보 객체 getter 를 한 번 연결한다. */
export const bindMixerInputOwner = (getter) => {
    getInfo = typeof getter === "function" ? getter : () => null;
};

/**
 * 믹서 입력 노드를 만들고 clone 과 쌍으로 등록한다.
 *
 * @param {AudioContext} audioCtx
 * @param {MediaStream}  sourceStream - 원본(이 함수가 clone 을 만든다)
 * @param {String}       role         - "mic" | "system"
 * @returns {MediaStreamAudioSourceNode}
 */
export const createMixerInput = (audioCtx, sourceStream, role) => {
    const clone = sourceStream.clone();
    let node = null;
    try {
        node = audioCtx.createMediaStreamSource(clone);
    } catch (err) {
        const failures = [];
        stopAllTracks(clone, failures, `${role}-rollback`);
        if (failures.length > 0) err.cleanupFailures = failures;
        log(`${role} 입력 노드 생성 실패, 만든 clone 을 반환했다(${err && err.message})`);
        throw err;
    }
    registry.set(node, { node, clone, role });
    log(`${role} 입력 등록(등록 ${registry.size})`);
    return node;
};

/** 종료 작업이 없거나, 이 노드가 종료 스냅샷에 속할 때만 정리할 수 있다(R-258). */
export const canReleaseMixerInput = (node) => {
    if (!node) return false;
    if (exitSnapshot === null) return true;
    if (exitSnapshot.nodes.has(node)) return true;
    log("종료 시작 뒤 등록된 입력이다. 이번 종료에서 정리하지 않는다.");
    return false;
};

/**
 * 역할별 정리에서 버리는 노드의 clone 을 반환한다.
 * @returns {Boolean} 실패가 없으면 true
 */
export const releaseMixerInput = (node) => {
    const failures = [];
    if (!node) return true;
    const entry = registry.get(node);
    if (entry) {
        releaseEntry(entry, failures);
    } else if (!releasedNodes.has(node)) {
        failures.push({ name: "mixer-input-unregistered", err: new Error("mixer-input-unregistered") });
    }
    recordFailures(failures);
    return failures.length === 0;
};

/**
 * 역할 노드를 버린다(clearVideoInput 의 시스템 입력 폐기 등, R-248).
 * 현재 노드를 disconnect·clone 반환·참조 null·flag 초기화한다.
 * @returns {Boolean} 실패가 없으면 true
 */
export const discardMixerInput = (role) => {
    const info = getInfo();
    const k = ROLE_KEYS[role];
    if (!info || !k) return true;
    const node = info[k.node];
    if (!node) return true;
    if (!canReleaseMixerInput(node)) return true;
    try {
        if (info[k.gain]) node.disconnect(info[k.gain]);
    } catch (err) {
        // 이미 끊긴 노드
    }
    const isOk = releaseMixerInput(node);
    info[k.node] = null;
    info[k.flag] = false;
    info[k.has] = false;
    log(`${role} 입력을 버렸다.`);
    return isOk;
};

/**
 * 20261030 화면 재선택 때 이전 system 입력을 동기로 교체 폐기한다 (web-relay R-249·R-263, milestone_42 §3.39.5).
 * 기존 `if (isSysConnectFlag) await clearSystemStreamSource()` 를 대신한다. 유효 판정 뒤 동기 구간에서만 부른다.
 * - 이전 system 노드는 flag 와 관계없이 폐기한다(flag=false 잔존 clone 누수 보완).
 * - 기존 호출 조건(flag true)이었고 mic 입력이 없을 때만, 확보해 둔 이전 sendAudioStream 을 정지·null 로 한다
 *   (clearSystemStreamSource 의 기존 정리와 같은 조건).
 * destination·Producer track·새 화면 stream·mic 는 건드리지 않는다.
 * @returns {Boolean} 실패가 없으면 true
 */
export const replaceSystemMixerInputForScreen = () => {
    const info = getInfo();
    if (!info) return true;
    const wasConnected = info.isSysConnectFlag === true;
    const prevSend = info.sendAudioStream;
    let isOk = discardMixerInput("system");
    if (wasConnected && info.micStreamSource === null && !info.isMicConnectFlag && prevSend) {
        const failures = [];
        stopAllTracks(prevSend, failures, "prev-sendAudioStream");
        recordFailures(failures);
        if (failures.length > 0) isOk = false;
        if (info.sendAudioStream === prevSend) {
            info.sendAudioStream = null;
            info.hasMicAudio = false;
            info.hasSystemAudio = false;
        }
        log("화면 교체: 이전 sendAudioStream 을 정리했다(mic 없음).");
    }
    return isOk;
};

/**
 * 20261030 마이크 장치 변경 등으로 새 마이크를 대입하기 직전, 이전 mic 입력과 이전 원본 마이크를 동기로 폐기한다
 * (web-relay R-249, milestone_42 §3.37.5). PC(믹서 destination 있음)에서만 부른다. 유효 판정 뒤 동기 구간에서만 부른다.
 * @param {MediaStream} nextStream - 새로 받은 원본 마이크(정지하지 않는다)
 * @returns {Boolean} 실패가 없으면 true
 */
export const replaceMicMixerInputForDevice = (nextStream) => {
    const info = getInfo();
    if (!info) return true;
    let isOk = discardMixerInput("mic");
    const prevRaw = info.rawAudioStream;
    if (prevRaw && prevRaw !== nextStream) {
        const failures = [];
        stopAllTracks(prevRaw, failures, "prev-rawAudioStream");
        recordFailures(failures);
        if (failures.length > 0) isOk = false;
        log("마이크 교체: 이전 원본 마이크를 정지했다.");
    }
    return isOk;
};

/** 기존 정리 함수의 false 를 종료 결과까지 남긴다(R-252). 종료 중이 아니면 기록만 한다. */
export const noteMixerCleanupFailure = (name) => {
    recordFailures([{ name, err: new Error(`${name}-false`) }]);
};

/** 종료 시작(onBegin) 때 동기로 부른다(R-257). */
export const beginMixerExitSnapshot = (token) => {
    const info = getInfo();
    const nodes = new Set(registry.keys());
    if (info) {
        [info.micStreamSource, info.systemStreamSource].forEach((n) => { if (n) nodes.add(n); });
    }
    const el = info ? info.myCamEl : null;
    const stream = el && el.srcObject ? el.srcObject : null;
    exitSnapshot = { token, nodes, myCam: stream ? { el, stream } : null, failures: [], released: false }; // 20261029 job 종료까지 스냅샷 유지 (R-259)
    log(`종료 스냅샷: 입력 ${nodes.size}, 마이캠 ${stream ? 1 : 0}`);
};

/**
 * 종료 finish 에서 스냅샷 전체를 반환한다. 덮어써진 이전 쌍도 포함한다.
 * 전역 참조·flag 는 현재 노드가 그 쌍일 때만 비운다.
 *
 * @param {Object}  token
 * @param {Object}  [options]
 * @param {Boolean} [options.skipMyCam] - 앱 웹뷰면 true(마이캠은 기존 relay/native 종료에 맡긴다)
 * @returns {Array<{name, err}>} 실패 목록
 */
export const releaseMixerExitSnapshot = (token, options = {}) => {
    const snap = exitSnapshot;
    if (snap === null || snap.token !== token) return [];
    const failures = snap.failures.splice(0);
    if (snap.released) return failures; // 20261029 조기 finish 뒤 실패만 전달하고 중복 반환하지 않음 (R-259)
    const info = getInfo();

    snap.nodes.forEach((node) => {
        const entry = registry.get(node);
        if (entry) {
            releaseEntry(entry, failures);
        } else if (!releasedNodes.has(node)) {
            failures.push({ name: "mixer-input-unregistered", err: new Error("mixer-input-unregistered") });
        }
        if (info) {
            Object.keys(ROLE_KEYS).forEach((role) => {
                const k = ROLE_KEYS[role];
                if (info[k.node] === node) {
                    info[k.node] = null;
                    info[k.flag] = false;
                    info[k.has] = false;
                }
            });
        }
    });

    if (options.skipMyCam !== true && snap.myCam !== null) {
        const { el, stream } = snap.myCam;
        stopAllTracks(stream, failures, "myCam");
        let detachedMyCam = false; // 20261029 같은 요소에 붙은 후속 스트림의 상태는 보존 (R-260)
        try {
            if (el.srcObject === stream) {
                el.srcObject = null;
                detachedMyCam = true;
            }
        } catch (err) {
            failures.push({ name: "myCam-detach", err });
        }
        if (detachedMyCam && info && info.myCamEl === el && info.videoInputInfo) {
            info.videoInputInfo.isMyCamRunning = false;
            info.videoInputInfo.myCamIndex = -1;
            info.videoInputInfo.myCamMediaInfo = { deviceId: null, groupId: null, label: null };
        }
    }

    snap.released = true; // 20261029 본문·획득 대기 중에도 후속 입력 제외·실패 기록 유지 (R-259)
    log(`종료 스냅샷 반환 끝. 남은 등록 ${registry.size}, 실패 ${failures.length}`);
    return failures;
};

/** 20261029 본문·finish·획득 대기 뒤, 보류 시작 전에 동기로 스냅샷을 닫는다 (R-259). */
export const endMixerExitSnapshot = (token) => {
    if (exitSnapshot !== null && exitSnapshot.token === token) {
        log(`종료 job 스냅샷을 닫는다. 반환 ${exitSnapshot.released}, 미수집 실패 ${exitSnapshot.failures.length}`);
        exitSnapshot = null;
    }
};

/** 시험용: 등록 수 */
export const getMixerInputCount = () => registry.size;
